/**
 * Reactive social state: the room's chat log and the currently animating
 * reactions.
 *
 * Kept out of `RoomState` because its lifecycle is different: chat and
 * reactions are *pushed* (`ServerMessage::Chat` / `Reaction`), not part of the
 * snapshot that `RoomState` applies verbatim, and reactions additionally expire
 * on a local timer. The rules and bounds themselves live in the pure `chat.ts`
 * / `reactions.ts` modules; this class only owns state, timers, and the send
 * path.
 *
 * Sending goes through the injected `run` — the same single command path every
 * other control uses — so permission checks, ack correlation, busy counters,
 * and rejection notices are not duplicated here. Nothing is echoed optimistically:
 * the sender renders its own message only when the server's broadcast arrives.
 */

import type { ChatMessage, ReactionKind, RoomSnapshot } from '../generated';
import type { CommandOutcome } from '../room/client';
import type { CommandIntent, ReactionEvent } from '../room/protocol';
import { appendChatMessage, checkChatText, mergeChatHistory } from './chat';
import {
  REACTION_LIFETIME_MS,
  REACTION_SEND_INTERVAL_MS,
  appendReactionBurst,
  pruneReactionBursts,
  reactionCooldownMs,
  type ReactionBurst,
} from './reactions';

/** Outcome of one attempted chat send. */
export type ChatSendResult =
  /** The local mirror of the server's rules refused it; nothing was sent. */
  | { readonly kind: 'refused'; readonly message: string | null }
  | { readonly kind: 'sent'; readonly outcome: CommandOutcome };

export class SocialState {
  /** Oldest first, bounded by `CHAT_LOG_LIMIT`. */
  messages = $state<readonly ChatMessage[]>([]);
  /** Reactions currently on screen, bounded by `REACTION_MAX_ACTIVE`. */
  bursts = $state<readonly ReactionBurst[]>([]);
  /** True while this client's own reaction cooldown is running. */
  reactionCoolingDown = $state(false);

  readonly #run: (intent: CommandIntent) => Promise<CommandOutcome>;

  #seq = 0;
  #lastReactionSentMs: number | null = null;
  #expiryTimer: number | undefined;
  #cooldownTimer: number | undefined;

  constructor(run: (intent: CommandIntent) => Promise<CommandOutcome>) {
    this.#run = run;
  }

  /**
   * Applies a snapshot's bounded chat history. Called on join *and* on every
   * later snapshot: the union by ID keeps a reconnect from duplicating
   * messages and from losing ones that arrived while the socket was down.
   */
  applySnapshot(snapshot: RoomSnapshot): void {
    this.messages = mergeChatHistory(this.messages, snapshot.recent_chat);
  }

  /** A new epoch is a different room; its chat is gone, not merged. */
  reset(): void {
    this.messages = [];
    this.bursts = [];
    this.#stopExpiry();
  }

  receiveChat(message: ChatMessage): void {
    this.messages = appendChatMessage(this.messages, message);
  }

  receiveReaction(event: ReactionEvent): void {
    this.#seq += 1;
    this.bursts = appendReactionBurst(this.bursts, event, performance.now(), this.#seq);
    this.#scheduleExpiry();
  }

  /**
   * Validates locally, then sends. A local refusal is reported as `refused`
   * (so the input can explain itself without a round trip); anything sent
   * carries the server's outcome — including a rejection, because the server
   * is the authority on its own bounds.
   */
  async sendChat(raw: string): Promise<ChatSendResult> {
    const checked = checkChatText(raw);
    if (!checked.ok) return { kind: 'refused', message: checked.message };
    return { kind: 'sent', outcome: await this.#run({ type: 'send-chat', text: checked.text }) };
  }

  /**
   * Sends one reaction unless this client's mirror of the server's rate limit
   * is still closed. The server remains authoritative and may reject anyway.
   */
  async sendReaction(kind: ReactionKind): Promise<CommandOutcome | null> {
    const now = performance.now();
    if (reactionCooldownMs(this.#lastReactionSentMs, now) > 0) return null;
    this.#lastReactionSentMs = now;
    this.#startCooldown();
    return this.#run({ type: 'send-reaction', kind });
  }

  dispose(): void {
    this.#stopExpiry();
    clearTimeout(this.#cooldownTimer);
    this.#cooldownTimer = undefined;
  }

  /**
   * One timer for the whole list, armed for the oldest burst's expiry. A
   * per-burst timer would scale with a flood — exactly the case this must stay
   * cheap for.
   */
  #scheduleExpiry(): void {
    if (this.#expiryTimer !== undefined) return;
    const oldest = this.bursts[0];
    if (oldest === undefined) return;
    const delay = Math.max(0, oldest.startedAtMs + REACTION_LIFETIME_MS - performance.now());
    this.#expiryTimer = setTimeout(() => {
      this.#expiryTimer = undefined;
      this.bursts = pruneReactionBursts(this.bursts, performance.now());
      this.#scheduleExpiry();
    }, delay);
  }

  #stopExpiry(): void {
    clearTimeout(this.#expiryTimer);
    this.#expiryTimer = undefined;
  }

  #startCooldown(): void {
    this.reactionCoolingDown = true;
    clearTimeout(this.#cooldownTimer);
    this.#cooldownTimer = setTimeout(() => {
      this.#cooldownTimer = undefined;
      this.reactionCoolingDown = false;
    }, REACTION_SEND_INTERVAL_MS);
  }
}
